import React from 'react';
import { Link } from 'react-router-dom';
import { Crosshair, CheckCircle, X } from 'lucide-react';
import { UseAutoStop } from '../hooks/useAutoStop';

/**
 * The one line on the Field page that says what auto-start is doing, and the "Done?" question that
 * comes up on leaving a yard.
 *
 * Michael, Nov 3 2026: "zero taps required; crew tools must add no more than ~30 s per stop." So this
 * is a strip, not a panel: no buttons to work it, nothing to dismiss in the ordinary case. The only
 * tap it ever asks for is the one that finishes a stop he has driven away from.
 */
export default function AutoStartStrip({ auto, nameOf }: {
  auto: UseAutoStop;
  /** Who the stop belongs to, for the prompt's words. */
  nameOf: (jobId: number) => string;
}) {
  const { settings, watching, words, gpsError, prompt, dismissPrompt, finishPrompt, busy } = auto;
  if (!settings.enabled) return null;
  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-brand-50 border border-brand-200 px-3 py-2 text-xs text-brand-900 flex items-center gap-2">
        <Crosshair className={`h-3.5 w-3.5 shrink-0 ${watching && !gpsError ? 'text-brand-600' : 'text-gray-400'}`} />
        <span className="flex-1 min-w-[10rem]">
          {gpsError || words}
          {watching && !gpsError && (
            <span className="text-brand-700/70"> · starts itself after {settings.dwell}s within {settings.radius} m</span>
          )}
        </span>
        <Link to="/settings#auto-start" className="font-semibold underline flex-shrink-0">Change</Link>
      </div>
      {prompt && (
        <div className="rounded-lg bg-green-50 border border-green-300 px-3 py-2.5 text-sm text-green-900 flex items-center gap-3 flex-wrap">
          <span className="flex-1 min-w-[12rem] font-semibold">
            Looks like you left {nameOf(prompt.jobId) || 'that yard'} — done there?
          </span>
          <button onClick={finishPrompt} disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
            <CheckCircle className="h-3.5 w-3.5" /> {busy ? 'Finishing…' : 'Yes, done'}
          </button>
          <button onClick={dismissPrompt} className="px-2 py-1.5 rounded-lg border border-green-300 text-xs font-semibold text-green-800 flex items-center gap-1">
            <X className="h-3.5 w-3.5" /> Not yet
          </button>
        </div>
      )}
    </div>
  );
}
